import React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, FreeMode } from 'swiper/modules'
import 'swiper/css'

// Halloween slot symbols (actual artwork from public/assets/img).
// Keys match the API resultMatrix `title` values (same set as HalloweenMachine).
const SYMBOLS = {
    spriteElement00: "ice-assets/game-img/spriteElement00.png",   // scarecrow
    spriteElement01: "ice-assets/game-img/spriteElement01.png", // monster
    spriteElement02: "ice-assets/game-img/spriteElement02.png",         // ghost
    spriteElement03: "ice-assets/game-img/spriteElement03.png",           // vampire
    spriteElement04: "ice-assets/game-img/spriteElement04.png",             // wizard / magic
    spriteElement05: "ice-assets/game-img/spriteElement05.png",           // pumpkin / halloween
    spriteElement06: "ice-assets/game-img/spriteElement06.png",               // witch
    spriteElement07: "ice-assets/game-img/spriteElement07.png",               // devil
    spriteElement08: "ice-assets/game-img/spriteElement08.png",         // cauldron
    spriteElement09: "ice-assets/game-img/spriteElement09.png",         // little witch
}
export const SYMBOL_KEYS = Object.keys(SYMBOLS)

// win animation for each symbol — normalized `-ani-strip` sheets (single row).
// frames = number of frames; aw/ah = one frame's pixel size (drives aspect);
// file = the sheet, when it isn't `${sym}-ani-strip.png`.
const SYMBOL_ANIM = {
    spriteElement00: { frames: 13, aw: 182, ah: 171 },
    spriteElement01: { frames: 14, aw: 182, ah: 171 },
    spriteElement02: { frames: 12, aw: 184, ah: 170 },
    // WILD — delivered at 92x85 a frame and went blurry when stretched; upscaled
    // 4x per frame with Real-ESRGAN (x4plus-anime), see scripts/build-upscaled-symbol-strips.py
    spriteElement03: { frames: 12, aw: 368, ah: 340 },
    spriteElement04: { frames: 13, aw: 182, ah: 171 },
    spriteElement05: { frames: 7, aw: 182, ah: 172 },
    spriteElement06: { frames: 8, aw: 182, ah: 172 },
    spriteElement07: { frames: 6, aw: 184, ah: 170 },
    // The fish roasting on the spit until it burns black (11 frames). Frame 0
    // is spriteElement08.png pixel for pixel, so the symbol doesn't jump when
    // the animation starts. The delivered sheet laid its 188px cells on a 184px
    // pitch (2028px wide), which made %-based steps drift; the strip is the
    // same cells cut at exactly 188px.
    spriteElement08: { file: 'spriteElement08-ani-fish.png', frames: 11, aw: 188, ah: 176 },
    spriteElement09: { frames: 13, aw: 182, ah: 171 },
}

// JOGADA GRÁTIS (spriteElement06) on the spin that awards free play: every one on
// the reels plays the strip for the awarded multiplier, "Nx" rising out of the
// pail (game-img/freeplaybonus.mp4). 1x has no strip of its own, so it keeps the
// plain win animation. The delivered sheets (freePlay 2.png, frrePlay3.png,
// freePlay 5.png) are drawn on a 182px pitch but carry 2 stray px at the end,
// which makes %-based steps drift and the frames shake; the -ani-strip copies
// are the same frames cut at exactly 182px.
// The strip does NOT loop. In the video the whole strip plays ONCE (the badge
// comes out of the pail, flies over his head and comes back down), then the
// badge stays beside his face with a small bob until the reels spin again. It
// never goes back to frame 0 (head down, no badge). `hold` is the two frames it
// bobs between: 9/10 on the 11-frame sheets, 5/4 on the 2x one, which stops
// with the badge up top.
const FREE_PLAY_SYMBOL = 'spriteElement06'
const FREE_PLAY_ANIM = {
    1: { file: 'spriteElement06-ani-strip.png', frames: 8, aw: 182, ah: 172 },
    2: { file: 'freePlay2-ani-strip.png', frames: 8, aw: 182, ah: 172, hold: [5, 4] },
    3: { file: 'freePlay3-ani-strip.png', frames: 11, aw: 182, ah: 172, hold: [9, 10] },
    5: { file: 'freePlay5-ani-strip.png', frames: 11, aw: 182, ah: 172, hold: [9, 10] },
}
const FRAME_MS = 90

// background-position-x of frame i on an n-frame strip sized n*100%
const framePos = (i, n) => `${(i / (n - 1)) * 100}%`

const freePlayAnimStyle = (fp) => {
    const style = {
        aspectRatio: `${fp.aw} / ${fp.ah}`,
        backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/game-img/${fp.file})`,
        backgroundSize: `${fp.frames * 100}% 100%`,
    }
    const intro = ((fp.frames * FRAME_MS) / 1000).toFixed(2)
    if (!fp.hold) {
        style.animation = `ice-sym-anim ${intro}s steps(${fp.frames}, jump-none) infinite`
        return style
    }
    style['--fp-a'] = framePos(fp.hold[0], fp.frames)
    style['--fp-b'] = framePos(fp.hold[1], fp.frames)
    style.animation = `ice-sym-anim ${intro}s steps(${fp.frames}, jump-none) 1 forwards, `
        + `ice-fp-hold ${(4 * FRAME_MS * 2) / 1000}s ${intro}s infinite`
    return style
}

// Halloween_20 has 20 pay-lines; every number badge is always on screen
const TOTAL_LINES = 20

// bonus-line-{n}.png has a black box baked in for the line's win value.
// These are the box centres (% of the line overlay) where that number is drawn,
// detected per image. Index 0 = line 1 … index 19 = line 20.
const BONUS_BOX = [
    { left: 8.5, top: 44.9 },   // 1
    { left: 91.4, top: 14.8 },  // 2
    { left: 91.4, top: 85.3 },  // 3
    { left: 8.9, top: 5.2 },    // 4
    { left: 7.6, top: 95.0 },   // 5
    { left: 91.4, top: 24.0 },  // 6
    { left: 91.4, top: 75.6 },  // 7
    { left: 91.9, top: 35.3 },  // 8
    { left: 8.0, top: 54.7 },   // 9
    { left: 34.3, top: 19.4 },  // 10
    { left: 8.5, top: 85.2 },   // 11
    { left: 91.0, top: 5.3 },   // 12
    { left: 92.3, top: 94.0 },  // 13
    { left: 91.4, top: 44.7 },  // 14
    { left: 8.0, top: 35.0 },   // 15
    { left: 8.0, top: 35.0 },   // 16
    { left: 8.0, top: 66.0 },   // 17
    { left: 8.0, top: 24.5 },   // 18
    { left: 8.5, top: 75.3 },   // 19
    { left: 91.2, top: 65.5 },  // 20
]

// 5 reels x 3 rows (column-major) matching the reference screenshot
export const INITIAL_REELS = [
    ["spriteElement00", "spriteElement01", "spriteElement00"],
    ["spriteElement02", "spriteElement03", "spriteElement03"],
    ["spriteElement04", "spriteElement03", "spriteElement00"],
    ["spriteElement05", "spriteElement00", "spriteElement02"],
    ["spriteElement00", "spriteElement06", "spriteElement07"],
]

// Reel frame images (5-column strips) sliced per column
const REEL_WHITE = "ice-assets/line/img_rodilho.png"       // stopped / result
const REEL_BLUE = "ice-assets/line/img_rodilho_freeplay.png" // spinning

// Symbols scrolled (transparent) during the spin so the blue frame shows behind
const SPIN_STRIP = [...SYMBOL_KEYS, ...SYMBOL_KEYS]

// Per-column background slice of a 5-column frame image
const reelFrameStyle = (ci, spinning) => ({
    backgroundImage: `url(${process.env.PUBLIC_URL}/${REEL_WHITE})`,
    backgroundSize: "500% 100%",
    backgroundPositionX: `${ci * 25}%`,
    backgroundPositionY: "0",
    backgroundRepeat: "no-repeat",
})

// Fast vertical looping reel — Halloween-20 style, symbols transparent over blue frame
const SpinAnimation = () => (
    <Swiper
        direction="vertical"
        slidesPerView={3}
        spaceBetween={0}
        loop={true}
        freeMode={true}
        speed={20}
        grabCursor={true}
        className="ice-swiper"
        autoplay={{ delay: 5, reverseDirection: true, disableOnInteraction: false }}
        modules={[Autoplay, FreeMode]}
    >
        {SPIN_STRIP.map((sym, i) => (
            <SwiperSlide key={i}>
                <img src={`${process.env.PUBLIC_URL}/${SYMBOLS[sym]}`} alt="" />
            </SwiperSlide>
        ))}
    </Swiper>
)

function IceMainContainer({
    reels = INITIAL_REELS,
    animateColumn = [false, false, false, false, false],
    lineNum = 1,
    winningLines = [],
    linePreview = true,
    // { lineNumber: bonusValue } — when set, the bonus-line-{n} overlay + its value show
    bonusLines = null,
    // winning symbol coordinates from the API ({ x: row, y: column }) — these blink
    highlightCells = [],
    // multiplier (1/2/3/5) of the free play this spin awarded, or null
    freePlayAnim = null,
}) {
    const freePlay = freePlayAnim ? FREE_PLAY_ANIM[freePlayAnim] || FREE_PLAY_ANIM[1] : null
    const isWinCell = (row, col) => highlightCells.some((c) => c.x === row && c.y === col)
    const anyWinCell = highlightCells.length > 0
    const hasWin = winningLines.length > 0
    // while any reel is rolling, hide the pay-line paths (like Halloween_20)
    const spinning = animateColumn.some(Boolean)
    const bonusEntries = bonusLines ? Object.entries(bonusLines) : []
    return (
        <div className='ice-main-container'>
            <div className="line-content">
                <div className=' position-relative w-100 h-100'>
                    <img className=' position-absolute w-100 h-100' src="ice-assets/line/no-dot.png" alt="" />
                    {/* every line number badge stays visible (Halloween_20's NumLine{n}); the
                        selected ones are lit, the rest sit dim in their circle */}
                    {Array.from({ length: TOTAL_LINES }, (_, i) => {
                        const n = i + 1
                        const selected = n <= lineNum
                        let cls = 'ice-num-off'
                        if (selected) cls = ''
                        if (hasWin && winningLines.includes(n)) cls = 'ice-line-win'
                        return (
                            <img
                                key={`num${n}`}
                                className={`position-absolute w-100 h-100 ${cls}`}
                                src={`ice-assets/line/num-${n}.png`}
                                alt=""
                            />
                        )
                    })}

                    {/* pay-line paths (Halloween_20 rules):
                        - hidden while the reels roll
                        - after a win: only the matching lines are drawn
                        - during line selection (preview): all selected lines
                        - otherwise (after a losing spin): nothing — only the number badges */}
                    {!spinning && (hasWin || linePreview) && Array.from({ length: lineNum }, (_, i) => {
                        const n = i + 1
                        if (hasWin && !winningLines.includes(n)) return null
                        return (
                            <img
                                key={`line${n}`}
                                className={`position-absolute w-100 h-100 ${hasWin ? 'ice-line-win' : ''}`}
                                src={`ice-assets/line/num-line-${n}.png`}
                                alt=""
                            />
                        )
                    })}

                    {/* bonus lines — the bonus-line-{n} overlay (line + black box) plus its
                        win value printed inside the box */}
                    {bonusEntries.map(([line, value]) => {
                        const n = Number(line)
                        const box = BONUS_BOX[n - 1]
                        return (
                            <React.Fragment key={`bonus${n}`}>
                                <img
                                    className='position-absolute w-100 h-100 ice-bonus-line'
                                    src={`ice-assets/line/bonus-line-${n}.png`}
                                    alt=""
                                />
                                {box && (
                                    <span
                                        className='ice-bonus-num'
                                        style={{ left: `${box.left}%`, top: `${box.top}%` }}
                                    >{value}</span>
                                )}
                            </React.Fragment>
                        )
                    })}
                </div>
            </div>
            <div className='ice-reels position-relative overflow-hidden '>
                <div className='ice-reels-main'>
                    <img className='position-absolute top-0 right-0 bottom-0 left-0 w-100 h-100' src="ice-assets/line/img_rodilho.png" alt="" />
                    {reels.map((col, ci) => (
                        <div className='ice-reel' key={`c${ci}`}>
                            {animateColumn[ci] ? (
                                <SpinAnimation />
                            ) : (
                                col.map((sym, ri) => {
                                    const win = anyWinCell && isWinCell(ri, ci)
                                    const anim = SYMBOL_ANIM[sym]
                                    const fp = freePlay && sym === FREE_PLAY_SYMBOL ? freePlay : null
                                    return (
                                        <div
                                            className={`ice-cell ${anyWinCell ? (win ? 'ice-cell-win' : 'ice-cell-dim') : ''}`}
                                            key={`c${ci}r${ri}`}
                                        >
                                            {fp ? (
                                                <div className='ice-symbol-anim' style={freePlayAnimStyle(fp)} />
                                            ) : win && anim ? (
                                                // winning symbol plays its animation instead of the static image
                                                <div
                                                    className='ice-symbol-anim'
                                                    style={{
                                                        aspectRatio: `${anim.aw} / ${anim.ah}`,
                                                        backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/game-img/${anim.file || `${sym}-ani-strip.png`})`,
                                                        backgroundSize: `${anim.frames * 100}% 100%`,
                                                        animation: `ice-sym-anim ${(anim.frames * 0.09).toFixed(2)}s steps(${anim.frames}, jump-none) infinite`,
                                                    }}
                                                />
                                            ) : (
                                                <img className='ice-symbol' src={`${process.env.PUBLIC_URL}/${SYMBOLS[sym]}`} alt={sym} />
                                            )}
                                        </div>
                                    )
                                })
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default IceMainContainer
